// Clasificaciones alternativas de un reply: niveles/subniveles que el staff
// también considera aceptables además de la etiqueta principal. Las reglas
// replican las de ReplySerializer en el backend, que es quien decide; acá sólo
// se adelantan para mostrar el error por fila antes de guardar.

const key = (level, sublevel) => `${level}${sublevel || ''}`;

const isComplete = (level, sublevel) => level !== '' && level != null && Boolean(sublevel);

// Devuelve un array con la clave de texto del error de cada fila (o '' si la
// fila está bien), alineado con `alternatives`.
export function validateAlternatives(mainLevel, mainSublevel, alternatives) {
    const hasMain = isComplete(mainLevel, mainSublevel);
    const mainKey = key(mainLevel, mainSublevel);
    const seen = new Set();
    return alternatives.map(({ level, sublevel }) => {
        if (!isComplete(level, sublevel)) return 'alt_incomplete';
        if (!hasMain) return 'alt_requires_main';
        const k = key(level, sublevel);
        if (k === mainKey) return 'alt_equals_main';
        if (seen.has(k)) return 'alt_duplicate';
        seen.add(k);
        return '';
    });
}

const KNOWN_ERRORS = new Set(['alt_incomplete', 'alt_requires_main', 'alt_equals_main', 'alt_duplicate']);

const firstMessage = (value) => (Array.isArray(value) ? value[0] : value);

// Traduce el error de `alternative_labels` que devuelve el backend a errores
// por fila, alineados con las `count` alternativas enviadas. Llega como
// ["alt_equals_main"] (regla sobre toda la lista, se marca en todas las filas)
// o como [{}, {level: [...]}] (error de campo en una fila puntual).
export function alternativeErrorsFromResponse(value, count) {
    if (!Array.isArray(value) && typeof value !== 'string') return [];
    const list = Array.isArray(value) ? value : [value];
    if (list.every((item) => typeof item === 'string')) {
        const code = KNOWN_ERRORS.has(list[0]) ? list[0] : 'alt_invalid';
        return Array.from({ length: count }, () => code);
    }
    return Array.from({ length: count }, (_, i) => {
        const rowErrors = list[i];
        if (!rowErrors || typeof rowErrors !== 'object' || Object.keys(rowErrors).length === 0) return '';
        const code = firstMessage(Object.values(rowErrors)[0]);
        return KNOWN_ERRORS.has(code) ? code : 'alt_invalid';
    });
}

// Compara la sugerencia de la IA con lo etiquetado: 'label' si coincide con la
// principal, 'alternative' si con alguna alternativa, 'none' si con ninguna, y
// null cuando no hay contra qué comparar (sin etiqueta o la IA sin nivel).
export function matchAgainstLabels(classification, mainLevel, mainSublevel, alternatives = []) {
    if (!classification || classification.status !== 'O' || classification.level == null) return null;
    if (!isComplete(mainLevel, mainSublevel)) return null;
    const aiKey = key(classification.level, classification.sublevel);
    if (aiKey === key(mainLevel, mainSublevel)) return 'label';
    if (alternatives.some((a) => isComplete(a.level, a.sublevel) && key(a.level, a.sublevel) === aiKey)) {
        return 'alternative';
    }
    return 'none';
}

export function alternativeLabelsText(alternatives) {
    if (!Array.isArray(alternatives)) return '';
    return alternatives.map((a) => key(a.level, a.sublevel)).join(', ');
}
